// pst/src/main/resources/monitor/src/store/slices/playlistSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse, post } from '../../api/request';
import type { RootState } from '../index';
import {ServerResponse} from "../../api/responses.ts";

// ── 재생 상태 상수 ────────────────────────────────────────────────
export const PlayStatus = {
  WAITING: 'QUEUE',   // 재생대기
  PLAYING: 'PLAY',   // 재생중
  ENDED:   'END',     // 재생종료
} as const;

export type PlayStatusType = typeof PlayStatus[keyof typeof PlayStatus];

// ── 신청곡 타입 정의 (추후 수정 예정) ─────────────────────────────
export interface PlaylistItem {
  playlistId: string;
  fbUid: string;
  type: string;
  artistNm: string;
  title: string;
  thumbnailUrl: string;
  duration: string;
  playStts: PlayStatusType;
  rqstPriority: string;
  musicVideoId: string;
  musicVideoFbUid: string;
  contentUrl: string;
  reqDt: string;
  contentId: string;
  playTms?: number[];
  playStTm?: number;
  playMaxTm?: number;
  playOrigin?: string;
  reqContentAt?: string;
  playStartDt?: string;
  playEndDt?: string;
}

export interface PlaylistSong {
  isQueue: boolean;
  playlistId: string;
  contentId: string;
  videoUrl: string;
  rqstPriority: string;
  musicVideoId: string;
  musicVideoFbUid: string;
  list: PlaylistItem[];
}

/**
 * 플레이리스트 음원 리스트 응답
 */
export interface PlaylistSongResponse extends ServerResponse<PlaylistSong> {}

// ── History 항목 (서버 전송 실패 시 저장) — 히스토리 비활성화 ────────
// export interface HistoryItem {
//   item:      PlaylistItem;
//   status:    PlayStatusType;
//   action:    'START' | 'END' | 'STATUS_CHANGE';
//   timestamp: string;
//   retryCount: number;
// }



// ── 서버 응답 타입 ────────────────────────────────────────────────
interface StatusUpdateResponse {
  code:    number;
  msg:     string;
  data:    unknown;
}

// ── 선택된 플레이리스트 타입 ────────────────────────────────────
export interface SelectedPlaylistInfo {
  id: string;
  title: string;
  description: string;
  youtubePlaylistId: string;
}

// ── State 정의 ────────────────────────────────────────────────────
interface PlaylistState {
  // 신청곡 목록 리스트
  playlist:        PlaylistItem[];
  // 현재 재생중인 신청곡 정보
  currentPlaying:  PlaylistItem | null;
  // 로딩 상태
  loading:         boolean;
  // 에러 메시지
  error:           string | null;
  // 서버 전송 실패 히스토리 — 비활성화
  // history:         HistoryItem[];
  // 히스토리 전송 중 여부 — 비활성화
  // syncingHistory:  boolean;
  // 선택된 플레이리스트 정보
  selectedPlaylist: SelectedPlaylistInfo | null;
}

const initialState: PlaylistState = {
  playlist:         [],
  currentPlaying:   null,
  loading:          false,
  error:            null,
  // history:          [],
  // syncingHistory:   false,
  selectedPlaylist: null,
};

// ── 히스토리 전송 async thunk — 비활성화 ─────────────────────────
// export const syncHistory = createAsyncThunk<
//     string[],
//     void,
//     { state: RootState; rejectValue: string }
// >(
//     'playlist/syncHistory',
//     async (_, { getState, rejectWithValue }) => {
//       const { history } = getState().playlist;
//
//       if (history.length === 0) {
//         return [];
//       }
//
//       const successIds: string[] = [];
//
//       for (const historyItem of history) {
//         try {
//           const response: ApiResponse<StatusUpdateResponse> = await post<StatusUpdateResponse>(
//               'pst',
//               '/playlist/status/sync',
//               {
//                 id:        historyItem.item.playlistId,
//                 playStts:  historyItem.status,
//                 action:    historyItem.action,
//                 timestamp: historyItem.timestamp,
//               }
//           );
//
//           if (response.data.code === 0) {
//             successIds.push(historyItem.item.playlistId + '_' + historyItem.timestamp);
//           }
//         } catch {
//           // sync 실패 — retryCount로 처리
//         }
//       }
//
//       if (successIds.length === 0 && history.length > 0) {
//         return rejectWithValue('히스토리 동기화 실패');
//       }
//
//       return successIds;
//     }
// );

// ── 신청곡 목록 가져오기 async thunk ──────────────────────────────
/**
 * 서버에서 신청곡 목록을 가져옵니다.
 */
export const fetchPlaylist = createAsyncThunk<
    PlaylistSong,
    void,
    { state: RootState; rejectValue: string; dispatch: any }
>(
    'playlist/fetchPlaylist',
    async (_arg, { rejectWithValue }) => {
      try {
        // 히스토리 동기화 비활성화
        // const { history, syncingHistory } = (getState() as RootState).playlist;
        // if (history.length > 0 && !syncingHistory) {
        //   await dispatch(syncHistory());
        // }

        const response: ApiResponse<PlaylistSongResponse> = await post<PlaylistSongResponse>(
            'pst',
            '/playlist/loadPlaylistSong',
            {}
        );

        if (response.data.code !== 0) {
          return rejectWithValue('신청곡 목록 조회 실패: ' + response.data.message);
        }

        return response.data.data;
      } catch (error: unknown) {
        if (error instanceof Error) {
          return rejectWithValue(error.message);
        }
        return rejectWithValue('신청곡 목록 조회 실패');
      }
    }
);

// ── 곡 재생 시작 서버 전송 async thunk ────────────────────────────
/**
 * 곡 재생이 시작되면 서버에 상태를 전송합니다.
 */
export const notifyPlayStart = createAsyncThunk<
    PlaylistItem,
    { item: PlaylistItem },
    { state: RootState; rejectValue: { item: PlaylistItem; error: string }; dispatch: any }
>(
    'playlist/notifyPlayStart',
    async ({ item }, { rejectWithValue }) => {
      try {
        // 히스토리 동기화 비활성화
        // await dispatch(syncHistory());

        const response: ApiResponse<StatusUpdateResponse> = await post<StatusUpdateResponse>(
            'pst',
            '/playlist/updateStart',
            {
              id:          item.playlistId,
              contentId:   item.contentId
            }
        );

        if (response.data.code !== 0) {
          return rejectWithValue({
            item,
            error: '재생 시작 상태 전송 실패: ' + response.data.msg,
          });
        }

        return {
          ...item,
          playStts:    PlayStatus.PLAYING,
          playStartDt: new Date().toISOString(),
        };
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : '재생 시작 상태 전송 실패';
        return rejectWithValue({ item, error: errorMessage });
      }
    }
);

// ── 곡 재생 종료 서버 전송 async thunk ────────────────────────────
/**
 * 곡 재생이 종료되면 서버에 상태를 전송합니다.
 */
export const notifyPlayEnd = createAsyncThunk<
    PlaylistItem,
    { item: PlaylistItem },
    { state: RootState; rejectValue: { item: PlaylistItem; error: string }; dispatch: any }
>(
    'playlist/notifyPlayEnd',
    async ({ item }, { rejectWithValue }) => {
      try {
        // 히스토리 동기화 비활성화
        // await dispatch(syncHistory());

        const response: ApiResponse<StatusUpdateResponse> = await post<StatusUpdateResponse>(
            'pst',
            '/playinfo/store',
            {
              playlistId:        item.playlistId,
              contentId:        item.contentId,
              playTms:  item.playTms,
              playStTm:  item.playStTm,
              playMaxTm:  item.playMaxTm,
              playOrigin: item.playOrigin,
              reqContentAt: item.reqContentAt,
            }
        );

        if (response.data.code !== 0) {
          return rejectWithValue({
            item,
            error: '재생 종료 상태 전송 실패: ' + response.data.msg,
          });
        }

        return {
          ...item,
          playStts:  PlayStatus.ENDED,
          playEndDt: new Date().toISOString(),
        };
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : '재생 종료 상태 전송 실패';
        return rejectWithValue({ item, error: errorMessage });
      }
    }
);

// ── 상태 변경 서버 전송 async thunk ───────────────────────────────
/**
 * 곡의 상태가 변경되면 서버에 전송합니다.
 */
export const updatePlayStatus = createAsyncThunk<
    { itemId: string; status: PlayStatusType },
    { itemId: string; status: PlayStatusType },
    { state: RootState; rejectValue: { itemId: string; status: PlayStatusType; error: string }; dispatch: any }
>(
    'playlist/updatePlayStatus',
    async ({ itemId, status }, { rejectWithValue }) => {
      try {
        // 히스토리 동기화 비활성화
        // await dispatch(syncHistory());

        const response: ApiResponse<StatusUpdateResponse> = await post<StatusUpdateResponse>(
            'pst',
            '/playlist/status/update',
            {
              id:       itemId,
              playStts: status,
            }
        );

        if (response.data.code !== 0) {
          return rejectWithValue({
            itemId,
            status,
            error: '상태 변경 전송 실패: ' + response.data.msg,
          });
        }

        return { itemId, status };
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : '상태 변경 전송 실패';
        return rejectWithValue({ itemId, status, error: errorMessage });
      }
    }
);

// ── Slice 정의 ────────────────────────────────────────────────────
const playlistSlice = createSlice({
  name: 'playlist',
  initialState,
  reducers: {
    // 신청곡 목록 초기화
    clearPlaylist(state) {
      state.playlist       = [];
      state.currentPlaying = null;
      state.error          = null;
    },

    // 에러 초기화
    clearError(state) {
      state.error = null;
    },

    // 히스토리 관련 reducers — 비활성화
    // removeHistoryItem(state, action: PayloadAction<string>) {
    //   state.history = state.history.filter(
    //       h => (h.item.playlistId + '_' + h.timestamp) !== action.payload
    //   );
    // },
    // clearHistory(state) {
    //   state.history = [];
    // },
    // trimHistory(state) {
    //   state.history = state.history.filter(h => h.retryCount <= 3);
    //   if (state.history.length > 50) {
    //     state.history = state.history.slice(-50);
    //   }
    // },

    // 로컬에서 상태만 변경 (서버 전송 없이)
    setLocalPlayStatus(state, action: PayloadAction<{ itemId: string; status: PlayStatusType }>) {
      const { itemId, status } = action.payload;
      const item = state.playlist.find(p => p.playlistId === itemId);

      if (item) {
        item.playStts = status;

        if (status === PlayStatus.PLAYING) {
          if (state.currentPlaying && state.currentPlaying.playlistId !== itemId) {
            const prevPlaying = state.playlist.find(p => p.playlistId === state.currentPlaying!.playlistId);
            if (prevPlaying) {
              prevPlaying.playStts = PlayStatus.WAITING;
            }
          }
          state.currentPlaying = { ...item, playStts: status };
        } else if (status === PlayStatus.ENDED && state.currentPlaying?.playlistId === itemId) {
          state.currentPlaying = null;
        }
      }
    },

    // 플레이리스트 선택
    setSelectedPlaylist(state, action: PayloadAction<SelectedPlaylistInfo | null>) {
      state.selectedPlaylist = action.payload;
    },

    // 선택된 플레이리스트 초기화
    clearSelectedPlaylist(state) {
      state.selectedPlaylist = null;
    },
  },
  extraReducers: (builder) => {
    // ── syncHistory — 비활성화 ─────────────────────────────────
    // builder.addCase(syncHistory.pending, (state) => {
    //   state.syncingHistory = true;
    // });
    // builder.addCase(syncHistory.fulfilled, (state, action) => {
    //   state.syncingHistory = false;
    //   state.history = state.history.filter(
    //       h => !action.payload.includes(h.item.playlistId + '_' + h.timestamp)
    //   );
    // });
    // builder.addCase(syncHistory.rejected, (state) => {
    //   state.syncingHistory = false;
    //   state.history = state.history
    //     .map(h => ({ ...h, retryCount: h.retryCount + 1 }))
    //     .filter(h => h.retryCount <= 3);
    // });

    // ── fetchPlaylist ────────────────────────────────────────
    builder.addCase(fetchPlaylist.pending, (state) => {
      state.loading = true;
      state.error   = null;
    });
    builder.addCase(fetchPlaylist.fulfilled, (state, action) => {
      state.loading  = false;
      state.playlist = action.payload.list;

      const playingItem = state.playlist.find(item => item.playStts === PlayStatus.PLAYING);
      state.currentPlaying = playingItem || null;
    });
    builder.addCase(fetchPlaylist.rejected, (state, action) => {
      state.loading = false;
      state.error   = action.payload || '신청곡 목록 조회 실패';
    });

    // ── notifyPlayStart ──────────────────────────────────────
    builder.addCase(notifyPlayStart.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(notifyPlayStart.fulfilled, (state, action) => {
      state.loading = false;

      if (state.currentPlaying) {
        const prevPlaying = state.playlist.find(p => p.playlistId === state.currentPlaying!.playlistId);
        if (prevPlaying) {
          prevPlaying.playStts = PlayStatus.WAITING;
        }
      }

      const item = state.playlist.find(p => p.playlistId === action.payload.playlistId);
      if (item) {
        item.playStts    = PlayStatus.PLAYING;
        item.playStartDt = action.payload.playStartDt;
      }

      state.currentPlaying = action.payload;
    });
    builder.addCase(notifyPlayStart.rejected, (state, action) => {
      state.loading = false;
      state.error   = action.payload?.error || '재생 시작 상태 전송 실패';

      // 히스토리 저장 비활성화
      // if (action.payload) {
      //   state.history.push({
      //     item:       action.payload.item,
      //     status:     PlayStatus.PLAYING,
      //     action:     'START',
      //     timestamp:  new Date().toISOString(),
      //     retryCount: 0,
      //   });
      // }

      // 로컬 상태는 변경
      if (action.payload) {
        const item = state.playlist.find(p => p.playlistId === action.payload!.item.playlistId);
        if (item) {
          item.playStts = PlayStatus.PLAYING;
        }
        state.currentPlaying = { ...action.payload.item, playStts: PlayStatus.PLAYING };
      }
    });

    // ── notifyPlayEnd ────────────────────────────────────────
    builder.addCase(notifyPlayEnd.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(notifyPlayEnd.fulfilled, (state, action) => {
      state.loading = false;

      const item = state.playlist.find(p => p.playlistId === action.payload.playlistId);
      if (item) {
        item.playStts  = PlayStatus.ENDED;
        item.playEndDt = action.payload.playEndDt;
      }

      if (state.currentPlaying?.playlistId === action.payload.playlistId) {
        state.currentPlaying = null;
      }
    });
    builder.addCase(notifyPlayEnd.rejected, (state, action) => {
      state.loading = false;
      state.error   = action.payload?.error || '재생 종료 상태 전송 실패';

      // 히스토리 저장 비활성화
      // if (action.payload) {
      //   state.history.push({
      //     item:       action.payload.item,
      //     status:     PlayStatus.ENDED,
      //     action:     'END',
      //     timestamp:  new Date().toISOString(),
      //     retryCount: 0,
      //   });
      // }

      if (action.payload) {
        const item = state.playlist.find(p => p.playlistId === action.payload!.item.playlistId);
        if (item) {
          item.playStts = PlayStatus.ENDED;
        }

        if (state.currentPlaying?.playlistId === action.payload.item.playlistId) {
          state.currentPlaying = null;
        }
      }
    });

    // ── updatePlayStatus ─────────────────────────────────────
    builder.addCase(updatePlayStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePlayStatus.fulfilled, (state, action) => {
      state.loading = false;
      const { itemId, status } = action.payload;

      const item = state.playlist.find(p => p.playlistId === itemId);
      if (item) {
        item.playStts = status;

        if (status === PlayStatus.PLAYING) {
          if (state.currentPlaying && state.currentPlaying.playlistId !== itemId) {
            const prevPlaying = state.playlist.find(p => p.playlistId === state.currentPlaying!.playlistId);
            if (prevPlaying) {
              prevPlaying.playStts = PlayStatus.WAITING;
            }
          }
          state.currentPlaying = { ...item, playStts: status };
        } else if (status === PlayStatus.ENDED && state.currentPlaying?.playlistId === itemId) {
          state.currentPlaying = null;
        }
      }
    });
    builder.addCase(updatePlayStatus.rejected, (state, action) => {
      state.loading = false;
      state.error   = action.payload?.error || '상태 변경 전송 실패';

      // 히스토리 저장 비활성화
      // if (action.payload) {
      //   const item = state.playlist.find(p => p.playlistId === action.payload!.itemId);
      //   if (item) {
      //     state.history.push({
      //       item:       { ...item },
      //       status:     action.payload.status,
      //       action:     'STATUS_CHANGE',
      //       timestamp:  new Date().toISOString(),
      //       retryCount: 0,
      //     });
      //   }
      // }

      if (action.payload) {
        const item = state.playlist.find(p => p.playlistId === action.payload!.itemId);
        if (item) {
          item.playStts = action.payload.status;

          if (action.payload.status === PlayStatus.PLAYING) {
            state.currentPlaying = { ...item };
          } else if (action.payload.status === PlayStatus.ENDED && state.currentPlaying?.playlistId === item.playlistId) {
            state.currentPlaying = null;
          }
        }
      }
    });
  },
});

export const {
  clearPlaylist,
  clearError,
  // removeHistoryItem,
  // clearHistory,
  // trimHistory,
  setLocalPlayStatus,
  setSelectedPlaylist,
  clearSelectedPlaylist,
} = playlistSlice.actions;

export default playlistSlice.reducer;

// ── Selectors ─────────────────────────────────────────────────────
export const selectPlaylist          = (state: RootState) => state.playlist.playlist;
export const selectCurrentPlaying    = (state: RootState) => state.playlist.currentPlaying;
export const selectPlaylistLoading   = (state: RootState) => state.playlist.loading;
export const selectPlaylistError     = (state: RootState) => state.playlist.error;
// export const selectHistory           = (state: RootState) => state.playlist.history;
// export const selectHasHistory        = (state: RootState) => state.playlist.history.length > 0;
// export const selectSyncingHistory    = (state: RootState) => state.playlist.syncingHistory;
export const selectSelectedPlaylist  = (state: RootState) => state.playlist.selectedPlaylist;
